import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, FileJson, ScanLine, Zap } from "lucide-react";
import { Dropzone } from "@/components/Dropzone";
import { SchemaPicker } from "@/components/SchemaPicker";
import { ResultView } from "@/components/ResultView";
import {
  extractDocument,
  getUsageStatus,
  type ExtractResult,
  type UsageStatus,
  ExtractError,
} from "@/lib/extract";
import type { SchemaId } from "@/lib/schemas";
import { toast } from "sonner";
import { captureException } from "@/lib/sentry";
import { useAuth } from "@/lib/auth";
import { UpgradeButton } from "@/components/UpgradeButton";
import { CreditPackButton } from "@/components/CreditPackButton";
import { trackEvent, trackConversion } from "@/lib/gtag";
import { cn } from "@/lib/utils";

// Per-session flag so we only fire the "first extraction" conversion once per session.
// Google Ads will still dedupe at the conversion-action level, but this avoids extra noise.
const SESSION_KEY_FIRST_EXTRACTION = "doclens.session.firstExtractionTracked";

/**
 * Tool-as-landing-page pattern. The dropzone is the first thing every visitor sees.
 *
 * Flow:
 *   - Anonymous visitor lands here, sees the dropzone immediately.
 *   - If they drop a file while signed out, we bounce to /login with an intent
 *     hint so AuthCallback can return them here with a friendly toast.
 *   - If signed in, we process normally.
 *   - Below the fold: value props + schema showcase for visitors who want to
 *     learn more before signing up.
 */

// Two-step UX:
//   idle       -> user hasn't picked a file
//   ready      -> file selected but NOT processed yet (user can switch
//                  schema, swap file, or click Process to commit)
//   processing -> extract API call in flight
//   done       -> result shown
//   error      -> something went wrong; show retry CTA
type Stage = "idle" | "ready" | "processing" | "done" | "error";
type QuotaWindow = "day" | "month";

export function UploadPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schema, setSchema] = useState<SchemaId>("receipt");
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>("idle");
  const [result, setResult] = useState<ExtractResult | null>(null);
  const [quotaError, setQuotaError] = useState<{ window: QuotaWindow } | null>(null);
  const [usage, setUsage] = useState<UsageStatus | null>(null);

  // If the user landed here right after auth callback with a "drop a file" hint, surface it
  useEffect(() => {
    const hint = sessionStorage.getItem("doclens.postLoginHint");
    if (hint === "drop-file") {
      sessionStorage.removeItem("doclens.postLoginHint");
      toast.success("You're signed in. Drop a document to process it.");
    }
  }, []);

  // On load (and after each extraction), check the user's quota usage. If they're
  // already out of plan documents AND have no credits, wall them proactively —
  // no point letting them stage a file and click Process just to be rejected.
  useEffect(() => {
    if (!user) {
      setUsage(null);
      return;
    }
    let cancelled = false;
    getUsageStatus()
      .then((status) => {
        if (cancelled || !status) return;
        setUsage(status);
        // Only auto-wall from the idle state — don't yank a user out of a
        // result they're viewing or an in-flight process.
        if (status.blocked) {
          setStage((cur) => {
            if (cur === "idle") {
              setQuotaError({ window: status.window });
              return "error";
            }
            return cur;
          });
        }
      })
      .catch(() => {
        /* non-fatal — the server still enforces the quota on extract */
      });
    return () => {
      cancelled = true;
    };
    // Re-run when the user changes or after an extraction completes (stage -> done).
  }, [user, stage === "done"]);

  /**
   * Called by the Dropzone when a file is picked. We DON'T process immediately
   * — that was burning the user's free daily extraction on the default schema
   * before they had a chance to pick the right one. Instead we stash the file
   * and show a confirmation panel with the schema picker still active.
   */
  function handleFile(uploadedFile: File) {
    // Anonymous visitor — bounce to login, ask them to re-drop after signup.
    if (!user) {
      sessionStorage.setItem("doclens.postLoginRedirect", "/");
      sessionStorage.setItem("doclens.postLoginHint", "drop-file");
      navigate("/login");
      return;
    }

    setFile(uploadedFile);
    setStage("ready");
    setResult(null);
    setQuotaError(null);
  }

  /**
   * Fires when the user clicks "Process document". Runs the extraction
   * with whatever schema is currently selected.
   */
  async function startProcessing() {
    if (!file) return;
    setStage("processing");
    setResult(null);
    setQuotaError(null);

    try {
      const extracted = await extractDocument({ file, schema });
      setResult(extracted);
      setStage("done");

      // Analytics + Google Ads conversion (first successful extraction per session)
      trackEvent("extraction_completed", {
        schema,
        billing: extracted.billing,
        processing_ms: extracted.processing_ms,
      });
      if (!sessionStorage.getItem(SESSION_KEY_FIRST_EXTRACTION)) {
        sessionStorage.setItem(SESSION_KEY_FIRST_EXTRACTION, "1");
        trackConversion(import.meta.env.VITE_GTAG_CONV_FIRST_EXTRACTION);
      }
    } catch (err) {
      if (err instanceof ExtractError && err.code === "quota_exceeded") {
        // Default to "day" since Free users hit this first; the actual window is
        // in the server response but we don't always parse it cleanly here.
        setQuotaError({ window: "day" });
        setStage("error");
        return;
      }

      const message = err instanceof ExtractError ? err.message : "Something went wrong. Try again.";
      toast.error(message, { duration: 8000 });
      setStage("error");

      // Don't ship these expected/user-visible errors to Sentry — they're noise.
      const expectedCodes = new Set([
        "quota_exceeded",
        "not_authenticated",
        "timeout",
        "network_error",
      ]);
      if (err instanceof ExtractError && expectedCodes.has(err.code)) return;
      captureException(err, {
        tags: { schema, file_type: file.type },
        contexts: {
          extraction: { file_name: file.name, file_size: file.size, schema },
        },
      });
    }
  }

  function reset() {
    setFile(null);
    setResult(null);
    setStage("idle");
    setQuotaError(null);
  }

  return (
    <div className="flex flex-col">
      {/* Hero + Dropzone — everyone sees this first */}
      <section className="mx-auto max-w-3xl px-4 py-12 md:py-16 w-full">
        {stage === "idle" && (
          <div className="space-y-6 animate-fade-in">
            <div className="text-center space-y-3">
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-balance">
                Turn any document into structured data.
              </h1>
              <p className="text-lg text-neutral-600 max-w-2xl mx-auto text-balance">
                AI-powered OCR for receipts, invoices, business cards, and more. Drop a file, get clean JSON in seconds.
              </p>
              {user && (
                <p className="text-sm text-neutral-500">
                  Signed in as <span className="font-medium">{user.email}</span>
                </p>
              )}
              {!user && (
                <p className="text-sm text-neutral-500">
                  Drop a file below — we'll ask for your email before processing. Free tier: 1 document/day, no card.
                </p>
              )}
            </div>

            {user && usage && (
              <UsageBadge used={usage.used} limit={usage.limit} window={usage.window} />
            )}
            <SchemaPicker value={schema} onChange={setSchema} />
            <Dropzone onFile={handleFile} />

            <p className="text-center text-xs text-neutral-500">
              JPG, PNG, or PDF · Max 5 MB · Files processed in memory, never stored
            </p>
          </div>
        )}

        {stage === "ready" && file && (
          <ReadyView
            file={file}
            schema={schema}
            onSchemaChange={setSchema}
            onProcess={startProcessing}
            onSwapFile={reset}
          />
        )}

        {stage === "processing" && file && <ProcessingView fileName={file.name} />}

        {stage === "done" && result && file && (
          <div className="space-y-6 animate-fade-in">
            {result.usage && (
              <UsageBadge
                used={result.usage.documents_used}
                limit={result.usage.documents_limit}
                window={result.usage.window}
              />
            )}
            <ResultView file={file} result={result} onReset={reset} />
          </div>
        )}

        {stage === "error" && quotaError && (
          <QuotaWall window={quotaError.window} onReset={reset} />
        )}

        {stage === "error" && !quotaError && (
          <div className="space-y-6 animate-fade-in text-center">
            <p className="text-lg text-neutral-700">Extraction failed.</p>
            <button
              onClick={reset}
              className="rounded-lg bg-brand text-white px-5 py-2.5 font-medium hover:bg-brand-700 transition"
            >
              Try another document
            </button>
          </div>
        )}
      </section>

      {/* Below-the-fold marketing — only show in idle state so it doesn't distract during processing */}
      {stage === "idle" && (
        <section className="border-t border-neutral-200 bg-white">
          <div className="mx-auto max-w-5xl px-4 py-16 space-y-16">
            <ValueProps />
            <SchemasShowcase />
            {!user && <SignupCTA />}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Confirmation step between file selection and extraction.
 * - Lets the user change schema after seeing the file
 * - Shows a file preview (filename + size, plus an image thumbnail when applicable)
 * - Primary CTA "Process document" runs the extraction
 * - Secondary "Choose a different file" goes back to idle
 *
 * Critical for the daily-free-tier user: a single extraction is precious,
 * we don't want to burn it on the wrong schema by accident.
 */
function ReadyView({
  file,
  schema,
  onSchemaChange,
  onProcess,
  onSwapFile,
}: {
  file: File;
  schema: SchemaId;
  onSchemaChange: (s: SchemaId) => void;
  onProcess: () => void;
  onSwapFile: () => void;
}) {
  const isImage = file.type.startsWith("image/");
  const previewUrl = useMemo(() => (isImage ? URL.createObjectURL(file) : null), [file, isImage]);
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="text-center space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold">Ready to process</h2>
        <p className="text-sm text-neutral-600">
          Double-check the schema below, then process the document.
        </p>
      </div>

      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-500 mb-2">
          Schema (you can still change this)
        </p>
        <SchemaPicker value={schema} onChange={onSchemaChange} />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white p-4 flex items-center gap-4">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={file.name}
            className="h-20 w-20 rounded-lg object-cover border border-neutral-200"
          />
        ) : (
          <div className="h-20 w-20 rounded-lg bg-neutral-100 flex items-center justify-center text-2xl">
            📄
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="font-medium text-neutral-900 truncate">{file.name}</p>
          <p className="text-sm text-neutral-500">
            {formatBytes(file.size)} · {file.type || "unknown type"}
          </p>
        </div>
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-3 justify-end">
        <button
          type="button"
          onClick={onSwapFile}
          className="inline-flex items-center justify-center rounded-lg border border-neutral-300 bg-white px-5 py-2.5 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition"
        >
          Choose a different file
        </button>
        <button
          type="button"
          onClick={onProcess}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand text-white px-6 py-2.5 text-sm font-semibold hover:bg-brand-700 transition"
        >
          Process document →
        </button>
      </div>

      <p className="text-center text-xs text-neutral-500">
        Files processed in memory, never stored. This will use one of your daily/monthly documents.
      </p>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ProcessingView({ fileName }: { fileName: string }) {
  // Multi-page PDFs and dense scans can take 60-90 seconds. After 15 seconds,
  // surface a hint so the user knows it's not stuck — silence is the worst UX.
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const slow = elapsed >= 15;
  const verySlow = elapsed >= 45;

  return (
    <div className="space-y-6 animate-fade-in text-center">
      <div className="inline-flex items-center gap-3 rounded-full bg-brand-50 px-4 py-2 text-brand-700 text-sm font-medium">
        <span className="h-2 w-2 rounded-full bg-brand-500 animate-pulse" />
        Processing {fileName}…
      </div>
      <div className="space-y-2 text-neutral-600 text-sm">
        <div>Detecting layout…</div>
        <div className={elapsed >= 5 ? "" : "opacity-70"}>Extracting fields…</div>
        <div className={elapsed >= 10 ? "opacity-70" : "opacity-40"}>Validating schema…</div>
      </div>
      {slow && (
        <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-1">
          <p className="font-medium">
            Still working — {elapsed}s elapsed
          </p>
          <p className="text-amber-800/90">
            {verySlow
              ? "Multi-page or scanned PDFs can take 60-90 seconds. Hang in there."
              : "Scanned PDFs and complex layouts can take 30+ seconds."}
          </p>
        </div>
      )}
    </div>
  );
}

function UsageBadge({
  used,
  limit,
  window,
}: {
  used: number;
  limit: number;
  window: QuotaWindow;
}) {
  const pct = Math.round((used / limit) * 100);
  const label = window === "day" ? "today" : "this month";
  return (
    <div className="rounded-lg bg-neutral-50 border border-neutral-200 px-4 py-2 text-sm flex items-center justify-between">
      <span className="text-neutral-700">
        <strong>{used}</strong> of <strong>{limit}</strong> documents used {label}
      </span>
      <div className="flex items-center gap-3">
        <div className="h-1.5 w-24 bg-neutral-200 rounded-full overflow-hidden">
          <div className={cn("h-full", pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-brand")} style={{ width: `${Math.min(pct, 100)}%` }} />
        </div>
        {pct >= 80 && (
          <Link to="/pricing" className="text-xs text-brand font-medium hover:underline">
            Upgrade
          </Link>
        )}
      </div>
    </div>
  );
}

function QuotaWall({ window, onReset }: { window: QuotaWindow; onReset: () => void }) {
  const headline =
    window === "day" ? "You've used your free document for today" : "You've hit your monthly limit";
  const body =
    window === "day"
      ? "Buy 10 credits to keep uploading right now, or upgrade for a higher daily limit. Or come back tomorrow."
      : "Buy credits for an immediate top-up, or pick a higher plan.";

  return (
    <div className="rounded-xl border-2 border-amber-200 bg-amber-50 p-8 text-center space-y-5 animate-fade-in">
      <h2 className="text-2xl font-bold text-amber-900">{headline}</h2>
      <p className="text-amber-800 max-w-md mx-auto">{body}</p>

      {/* Credit pack: fastest path back to uploading */}
      <div className="max-w-md mx-auto">
        <CreditPackButton pack="quick" variant="primary">
          Quick pack — $5 for 10 documents
        </CreditPackButton>
        <p className="text-xs text-amber-700/80 mt-1.5">
          One-time charge · Credits never expire · Works on any plan
        </p>
      </div>

      <div className="flex items-center gap-2 max-w-md mx-auto">
        <div className="flex-1 h-px bg-amber-200" />
        <span className="text-xs text-amber-700">or subscribe</span>
        <div className="flex-1 h-px bg-amber-200" />
      </div>

      <div className="grid sm:grid-cols-2 gap-3 max-w-md mx-auto">
        <UpgradeButton plan="starter" variant="secondary">
          Starter — $9/mo · 50 docs
        </UpgradeButton>
        <UpgradeButton plan="pro" variant="secondary">
          Pro — $29/mo · 200 docs
        </UpgradeButton>
      </div>

      <div className="flex items-center justify-center gap-3 pt-1">
        <Link to="/pricing" className="text-xs text-amber-800 underline hover:text-amber-900">
          See all options
        </Link>
        <span className="text-amber-300">·</span>
        <button onClick={onReset} className="text-xs text-amber-800 underline hover:text-amber-900">
          Close
        </button>
      </div>
    </div>
  );
}

// =====================================================================
// Below-the-fold marketing content
// =====================================================================

function ValueProps() {
  return (
    <div className="grid md:grid-cols-3 gap-6">
      <ValueProp
        icon={<Zap className="h-5 w-5 text-brand" />}
        title="3–8 second results"
        body="Drop a file, get structured JSON. No SDK, no AWS account, no integration."
      />
      <ValueProp
        icon={<FileJson className="h-5 w-5 text-brand" />}
        title="Schema-aware"
        body="Pick a template, get clean JSON with the fields you expect — not a raw text dump."
      />
      <ValueProp
        icon={<ScanLine className="h-5 w-5 text-brand" />}
        title="Privacy-first"
        body="Files are processed in memory and never stored on our servers. No training on your data."
      />
    </div>
  );
}

function ValueProp({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <div className="space-y-2">
      <div className="inline-flex items-center gap-2 font-medium">
        {icon} {title}
      </div>
      <p className="text-sm text-neutral-600">{body}</p>
    </div>
  );
}

const SCHEMA_CARDS = [
  { icon: "🧾", label: "Receipts", desc: "Vendor, date, totals, line items" },
  { icon: "📄", label: "Invoices", desc: "Numbers, dates, bill-to, line items, terms" },
  { icon: "💼", label: "Business Cards", desc: "Name, title, company, contact info" },
  { icon: "📝", label: "Free Text", desc: "Generic OCR — extract all text from any document" },
];

function SchemasShowcase() {
  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-center">Built for the documents you actually handle</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {SCHEMA_CARDS.map((s) => (
          <div key={s.label} className="rounded-lg border border-neutral-200 bg-white p-4 text-center">
            <div className="text-3xl mb-2">{s.icon}</div>
            <div className="font-medium text-sm">{s.label}</div>
            <div className="text-xs text-neutral-500 mt-1">{s.desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SignupCTA() {
  return (
    <section className="rounded-2xl bg-neutral-900 text-white p-8 md:p-12 text-center space-y-4">
      <h2 className="text-3xl font-bold">Try it free, right now.</h2>
      <p className="text-neutral-300 max-w-md mx-auto">
        Drop a document above. We'll ask for your email before processing — that's it. Free tier:
        1 document per day, no card required.
      </p>
      <ul className="text-sm text-neutral-400 space-y-1 pt-3">
        <li className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" /> No credit card</li>
        <li className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" /> Cancel anytime</li>
        <li className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4" /> All 4 schemas included</li>
      </ul>
    </section>
  );
}
